import {
  AlertTriangle,
  BadgeCheck,
  Building2,
  CalendarDays,
  Euro,
  FilePen,
  FileUp,
  Globe,
  Hash,
  Mail,
  MapPin,
  PartyPopper,
  Phone,
  Shield,
  ShieldAlert,
  TruckIcon,
  User,
  Users,
} from 'lucide-react';
import type {
  ConditionalVisibility,
  Field,
  FormSections,
  FormSectionsWithShortcuts,
  KorintModulesProductsConfig,
} from 'korint-sdk/react';
import { ContactKey } from '@folio/utils/customerDisplayName';
import { centsToEuros, eurosToCents } from '@folio/utils/amount-utils';
import {
  customerControllerEditCustomer,
  CustomFieldDto,
  ManualQuoteComponentDto,
  ManualQuotePercentageDto,
  policyControllerPushPolicyEvent,
  PolicyDto,
} from 'korint-sdk';
import { InvoicesScheduleModule } from '@folio/modules';
import z, { type ZodTypeAny } from 'zod';

const showWhenCompanyBeingCreated = [
  {
    fieldName: 'isCompanyBeingCreated',
    condition: 'equals',
    value: 'true',
  },
] as const satisfies ConditionalVisibility[];

const companySections: FormSectionsWithShortcuts = [
  {
    title: 'Entreprise',
    fields: [
      {
        name: 'isCompanyBeingCreated',
        required: false,
        type: 'radio',
        display: 'toggle' as const,
        options: [
          { value: 'true', label: 'Oui' },
          { value: 'false', label: 'Non' },
        ],
        icon: Building2,
        isCustomField: true,
        apiDataAccessor: apiData =>
          apiData.customFields?.isCompanyBeingCreated ? 'true' : 'false',
        formDataAccessor: formData => formData.isCompanyBeingCreated === 'true',
      },
      {
        name: 'companySiret',
        type: 'company',
        required: true,
        isCustomField: true,
        displayCompanyInformation: true,
        icon: Building2,
        apiDataAccessor: apiData =>
          apiData.customFields?.companySiret
            ? {
                siret: apiData?.customFields?.companySiret,
                name: apiData?.customFields?.companyName,
                location: {
                  postCode: apiData?.customFields?.companyHeadquarterPostcode,
                },
                nafLabel: apiData?.customFields?.companyNafLabel,
              }
            : null,
        formDataAccessor: 'companySiret.siret',
        showWhen: [
          {
            fieldName: 'isCompanyBeingCreated',
            condition: 'equals',
            value: 'false',
          },
        ],
      },
      {
        name: 'companyName',
        required: true,
        type: 'text',
        icon: Building2,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterAddress',
        required: true,
        type: 'address',
        icon: MapPin,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterPostcode',
        required: true,
        type: 'postcode',
        countries: ['FR'],
        icon: Hash,
        colSpan: 4 as const,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyHeadquarterCity',
        required: true,
        type: 'city',
        icon: Building2,
        colSpan: 8 as const,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyLegalStatus1',
        required: true,
        type: 'text',
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyCreationDate',
        required: true,
        type: 'date',
        icon: CalendarDays,
        isCustomField: true,
        showWhen: showWhenCompanyBeingCreated,
      },
      {
        name: 'companyActivity',
        required: true,
        type: 'text',
        icon: Building2,
      },
    ],
  },
  {
    title: "Informations financières de l'année dernière ou projection",
    fields: [
      { name: 'lastExerciseOrForecastRevenue', icon: Euro, colSpan: 6 as const },
      { name: 'yearOfRevenue', icon: CalendarDays, colSpan: 6 as const },
    ],
  },
];

const signatorySections: FormSectionsWithShortcuts = [
  {
    title: 'Signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User } },
      {
        name: 'contactFirstName',
        icon: User,
        colSpan: 6 as const,
      },
      {
        name: 'contactLastName',
        icon: User,
        colSpan: 6 as const,
      },
      {
        name: 'contactAddress',
        type: 'address',
        required: true,
        isCustomField: true,
        icon: MapPin,
      },
      {
        name: 'contactPostCode',
        type: 'postcode',
        countries: ['FR'],
        required: true,
        isCustomField: true,
        icon: Hash,
        colSpan: 4 as const,
      },
      {
        name: 'contactCity',
        type: 'city',
        icon: Building2,
        colSpan: 8 as const,
      },
      {
        name: 'contactEmail',
        icon: Mail,
        colSpan: 6 as const,
      },
      {
        name: 'contactPhoneNumber',
        icon: Phone,
        colSpan: 6 as const,
      },
      { name: 'contactBirthDate', icon: CalendarDays, colSpan: 4 as const },
      {
        name: 'contactBirthCity',
        type: 'city',
        addressGroup: 'contactBirth',
        icon: MapPin,
        colSpan: 4 as const,
      },
      {
        name: 'contactBirthCountry',
        type: 'country',
        addressGroup: 'contactBirth',
        icon: Globe,
        colSpan: 4 as const,
      },
    ],
  },
];

const companyNonDelegatedSections: FormSectionsWithShortcuts = [
  {
    title: "Informations financières de l'année dernière ou projection",
    fields: [
      { name: 'lastExerciseOrForecastRevenue', icon: Euro, colSpan: 6 as const },
      { name: 'yearOfRevenue', icon: CalendarDays, colSpan: 6 as const },
    ],
  },
];

const beneficialOwnersSections: FormSectionsWithShortcuts = [
  {
    title: 'Nombre de bénéficiaires effectifs',
    fields: [
      {
        name: 'numberOfBeneficialOwners',
        type: 'number' as const,
        display: 'cards' as const,
        required: true,
        isCustomField: true,
        min: 0,
        max: 5,
        icon: Users,
        ignoreInPayload: true,
        apiDataAccessor: customer =>
          Object.entries(customer.customFields ?? {}).filter(
            ([key, value]) => key.startsWith('beneficialOwnerFirstName') && Boolean(value),
          ).length,
      },
    ],
  },
  ...Array.from({ length: 5 }, (_, index) => ({
    title: `Bénéficiaire effectif ${index + 1}`,
    fields: [
      {
        name: `beneficialOwnerFirstName${index + 1}`,
        required: true,
        clearable: true,
        icon: User,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'Michel',
      },
      {
        name: `beneficialOwnerLastName${index + 1}`,
        required: true,
        clearable: true,
        icon: User,
        colSpan: 6 as const,
        DEPRECATED_placeholder: 'Dupont',
      },
      {
        name: `beneficialOwnerDateOfBirth${index + 1}`,
        type: 'date' as const,
        required: true,
        clearable: true,
        icon: CalendarDays,
        colSpan: 4 as const,
      },
      {
        name: `beneficialOwnerBirthCity${index + 1}`,
        type: 'city' as const,
        addressGroup: `beneficialOwnerBirth${index + 1}`,
        required: true,
        clearable: true,
        icon: MapPin,
        colSpan: 4 as const,
        DEPRECATED_placeholder: 'Paris',
      },
      {
        name: `beneficialOwnerBirthCountry${index + 1}`,
        type: 'country' as const,
        addressGroup: `beneficialOwnerBirth${index + 1}`,
        required: true,
        clearable: true,
        icon: Globe,
        colSpan: 4 as const,
        DEPRECATED_placeholder: 'France',
      },
    ],
    showWhen: [
      {
        fieldName: 'numberOfBeneficialOwners',
        condition: 'greaterThanOrEquals' as const,
        value: index + 1,
      },
    ],
  })),
];

const KILLING_MESSAGE = 'Il est impossible de souscrire une police dans ce cas';

const killingIfFalse = {
  blockingValue: 'false',
  blockingErrorMessage: KILLING_MESSAGE,
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(z.literal('true', { errorMap: () => ({ message: KILLING_MESSAGE }) })),
};

const killingIfTrue = {
  blockingValue: 'true',
  blockingErrorMessage: KILLING_MESSAGE,
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(z.literal('false', { errorMap: () => ({ message: KILLING_MESSAGE }) })),
};

const questionsSections: FormSectionsWithShortcuts = [
  {
    title: 'Informations complémentaires',
    fields: [
      { name: 'specificQuestionGenerali2', display: 'toggle' as const, icon: AlertTriangle },
      { name: 'specificQuestionGenerali3', display: 'toggle' as const, icon: Shield },
      { name: 'specificQuestionGenerali4', display: 'toggle' as const, icon: Globe },
      {
        name: 'specificQuestionGenerali5',
        display: 'toggle' as const,
        icon: ShieldAlert,
        ...killingIfFalse,
      },
      {
        name: 'specificQuestionGenerali6',
        display: 'toggle' as const,
        icon: ShieldAlert,
        ...killingIfTrue,
      },
      {
        name: 'specificQuestionGenerali7',
        display: 'toggle' as const,
        icon: ShieldAlert,
        ...killingIfFalse,
      },
      {
        name: 'specificQuestionGenerali8',
        display: 'toggle' as const,
        icon: ShieldAlert,
        ...killingIfFalse,
      },
      { name: 'specificQuestionGenerali9', display: 'toggle' as const, icon: ShieldAlert },
    ],
  },
];

const premiumFormDataAccessor = (data: Record<string, unknown>) => {
  const amountWithFeesPreTaxes =
    eurosToCents(data.premiumAmountWithIncludedWholesaleFee as number | undefined) ?? 0;

  const includedWholesaleFees = eurosToCents(data.includedWholesaleFees as number | undefined) ?? 0;

  const totalTaxes = eurosToCents(data.premiumTotalTaxes as number | undefined) ?? 0;

  return {
    manualPremiumComponent: {
      id: 'PREMIUM',
      chargeType: 'RECURRING',
      premium: {
        amountPreFeesAndTaxes: amountWithFeesPreTaxes - includedWholesaleFees,
        totalFees: includedWholesaleFees,
        totalTaxes,
      },
      type: 'PERIL',
    } satisfies ManualQuoteComponentDto,
  };
};

const manualQuoteSections = [
  {
    title: 'Prime',
    fields: [
      {
        name: 'premiumAmountWithIncludedWholesaleFee',
        required: true,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const component = data?.quote?.components?.find(component => component.id === 'PREMIUM');

          if (!component) return;

          return centsToEuros(component.premium.amountWithFeesPreTaxes);
        },
        formDataAccessor: premiumFormDataAccessor,
      },
      {
        name: 'includedWholesaleFees',
        required: true,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const component = data?.quote?.components?.find(component => component.id === 'PREMIUM');

          if (!component) return;

          return centsToEuros(component.premium.totalFees);
        },
        formDataAccessor: premiumFormDataAccessor,
      },
      {
        name: 'premiumTotalTaxes',
        required: true,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const totalTaxes = data?.quote?.components?.find(component => component.id === 'PREMIUM');

          if (!totalTaxes) return;

          return centsToEuros(totalTaxes.premium.totalTaxes);
        },
        formDataAccessor: premiumFormDataAccessor,
      },
    ] as const satisfies Array<Field>,
  },
] as const satisfies FormSectionsWithShortcuts;

const additionalQuoteComponentsSections = [
  {
    title: 'Frais grossistes',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'wholesaleAdditionalCommissionPercentage',
        required: false,
        type: 'number',
        integer: true,
        max: 100,
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleAdditionalCommission = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_ADDITIONAL_COMMISSION',
          );

          const premium = data?.quote?.components?.find(c => c.id.includes('PREMIUM'));

          if (!wholesaleAdditionalCommission || !premium?.premium.amountPreFeesAndTaxes) return;

          return Math.round(
            (wholesaleAdditionalCommission.premium.amountWithFeesAndTaxes /
              premium.premium.amountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const percentage = data.wholesaleAdditionalCommissionPercentage as number | undefined;

          // Ok because it is a string, else we would have to handle property zero value
          if (!percentage) return {};

          return {
            wholesaleAdditionalCommissionComponent: {
              componentIds: ['PREMIUM'],
              id: 'WHOLESALE_ADDITIONAL_COMMISSION',
              type: 'FEE',
              chargeType: 'RECURRING',
              amountPropertiesFrom: ['amountPreFeesAndTaxes'],
              percentage,
            } satisfies ManualQuotePercentageDto,
          };
        },
      },
      {
        name: 'wholesaleSubscriptionFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_SUBSCRIPTION_FEE',
          );

          if (!wholesaleSubscriptionFee) return;

          return centsToEuros(wholesaleSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.wholesaleSubscriptionFee as number | undefined;

          // Ok because it is a string, else we would have to handle property zero value
          if (!totalFees) return {};

          return {
            wholesaleSubscriptionFeeComponent: {
              id: 'WHOLESALE_SUBSCRIPTION_FEE',
              type: 'FEE',
              chargeType: 'ONE_TIME',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) as number,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
  {
    title: 'Frais distributeurs',
    forTypes: ['WHOLESALE', 'RETAIL'],
    fields: [
      {
        name: 'intermediaryCommission',
        required: false,
        type: 'number',
        integer: true,
        max: 100,
        apiDataAccessor: (data: PolicyDto) => {
          const intermediaryCommission = data?.quote?.components?.find(
            c => c.id === 'RETAIL_COMMISSION',
          );

          const premium = data?.quote?.components?.find(c => c.id.includes('PREMIUM'));

          if (!intermediaryCommission || !premium) return;

          return Math.round(
            (intermediaryCommission.premium.amountWithFeesAndTaxes /
              premium.premium.amountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: data => {
          const percentage = data.intermediaryCommission as number | undefined;

          // Ok because it is a string, else we would have to handle property zero value
          if (!percentage) return {};

          return {
            intermediaryCommissionPercentage: {
              componentIds: ['PREMIUM'],
              id: 'RETAIL_COMMISSION',
              type: 'FEE',
              chargeType: 'RECURRING',
              amountPropertiesFrom: ['amountPreFeesAndTaxes'],
              percentage: (data.intermediaryCommission ?? 0) as number,
            } satisfies ManualQuotePercentageDto,
          };
        },
      },
      {
        name: 'intermediarySubscriptionFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const retailSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'RETAIL_SUBSCRIPTION_FEE',
          );

          if (!retailSubscriptionFee) return;

          return centsToEuros(retailSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.intermediarySubscriptionFee as number | undefined;

          // Ok because it is a string, else we would have to handle property zero value
          if (!totalFees) return {};

          return {
            intermediarySubscriptionFeeComponent: {
              id: 'RETAIL_SUBSCRIPTION_FEE',
              type: 'FEE',
              chargeType: 'ONE_TIME',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) as number,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
      {
        name: 'intermediaryManagementFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const intermediaryCommission = data?.quote?.components?.find(
            c => c.id === 'RETAIL_MANAGEMENT_FEE',
          );

          if (!intermediaryCommission) return;

          return centsToEuros(intermediaryCommission?.premium?.amountWithFeesAndTaxes) ?? 0;
        },
        formDataAccessor: data => {
          const totalFees = data.intermediaryManagementFee as number | undefined;

          // Ok because it is a string, else we would have to handle property zero value
          if (!totalFees) return {};

          return {
            intermediaryManagementFeeComponent: {
              id: 'RETAIL_MANAGEMENT_FEE',
              type: 'FEE',
              chargeType: 'RECURRING',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
  {
    title: 'Autres frais',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'renewalFee',
        required: false,
        type: 'number',
        apiDataAccessor: (data: PolicyDto) => {
          const component = data?.quote?.components?.find(c => c.id === 'RENEWAL_FEE');

          if (!component) return;

          return centsToEuros(component?.premium?.totalFees) ?? 0;
        },
        formDataAccessor: data => {
          const renewalFee = data.renewalFee as number | undefined;

          // Ok because it is a string, else we would have to handle property zero value
          if (!renewalFee) return {};

          return {
            renewalFeeComponent: {
              id: 'RENEWAL_FEE',
              type: 'FEE',
              chargeType: 'SPOT',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: Math.round(eurosToCents(renewalFee) ?? 0),
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
] as const satisfies Array<FormSections[number] & { forTypes?: string[] }>;

const productCustomPath = '/transport-marchandises-generali';

const policyHolderInvitationRole = ['DRIVER'];

export const tutassurTransportMarchandisesGeneraliConfig = {
  display: {
    category: 'transport',
    icon: TruckIcon,
    badges: ['online'],
  },
  productCustomPath,
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'documents-upload',
        type: 'documentUploadFunnel' as const,
        context: 'policy' as const,
        icon: FileUp,
      },
      {
        // Needed so an asset exists for the quote step, so we can fill the startedAt field
        // Needs to be first to avoid issues when clicking "previous" from customer step
        path: 'asset',
        type: 'emptyAssetFunnel',
        hidden: true,
      },
      {
        path: 'company',
        type: 'customerFunnel',
        icon: Building2,
        sections: companySections,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: signatorySections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'questions',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: questionsSections,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        assetSections: [
          {
            title: "Date de prise d'effet de l'assurance",
            fields: [{ name: 'startedAt', min: 'now' }],
          },
        ],
        additionalQuoteComponentsSections,
        manualQuoteSections,
        nonDelegatedDocuments: ['SUBSCRIPTION_FORM'],
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'beneficial-owners',
        type: 'customerFunnel',
        icon: Users,
        sections: beneficialOwnersSections,
        afterSubmit: async (data, { customerId, branchId }) => {
          const numberOfBeneficialOwners = Number.parseInt(
            data.numberOfBeneficialOwners as string,
            10,
          );

          const fieldsToUpdate: CustomFieldDto[] = Array.from(
            { length: 5 - numberOfBeneficialOwners },
            (_, index) => [
              `beneficialOwnerFirstName${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerLastName${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerDateOfBirth${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerBirthCity${numberOfBeneficialOwners + index + 1}`,
              `beneficialOwnerBirthCountry${numberOfBeneficialOwners + index + 1}`,
            ],
          )
            .flat()
            .map(key => ({ key, value: '' }));

          if (fieldsToUpdate.length > 0) {
            await customerControllerEditCustomer(
              customerId,
              { customFields: fieldsToUpdate },
              { branchId },
            );
          }
        },
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: true },
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },
  mtaFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'company',
        type: 'customerFunnel',
        icon: Building2,
        sections: companySections,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: signatorySections,
      },
      {
        path: 'questions',
        type: 'policyFunnel',
        icon: ShieldAlert,
        sections: questionsSections,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        manualQuoteSections,
        additionalQuoteComponentsSections,
        nonDelegatedDocuments: ['SUBSCRIPTION_FORM'],
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}`,
      },
    ],
  },
  policyholderRenewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: companyNonDelegatedSections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId }) => `/policyholder/${policyId}/information`,
      },
    ],
  },
  renewalFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'customer',
        type: 'customerFunnel',
        sections: companyNonDelegatedSections,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        manualQuoteSections,
        additionalQuoteComponentsSections,
        nonDelegatedDocuments: ['SUBSCRIPTION_FORM'],
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'renewal-end',
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {},
        redirectTo: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}`,
      },
    ],
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: false,
    quoteDerogationFunnelModule: {
      afterEndPath: '/derogations',
      steps: [
        {
          path: 'quote',
          type: 'quoteFunnel',
          manualQuoteSections,
          additionalQuoteComponentsSections,
          nonDelegatedDocuments: ['SUBSCRIPTION_FORM'],
        },
      ],
    },
  },
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'TOTAL_PREMIUM', variant: 'dimmed' }] },
      { lines: [{ key: 'BROKERAGE_FEES' }] },
      { lines: [{ key: 'MANAGEMENT_FEE' }] },
      { lines: [{ key: 'SUBSCRIPTION_FEE' }, { key: 'RENEWAL_FEE', tooltip: true }] },
    ],
  },
  customerInformations: {
    fieldsConfig: [
      'contactCivility',
      { key: 'contactFirstName', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', contactKey: ContactKey.LAST_NAME },
      'contactEmail',
      'contactPhoneNumber',
      'contactBirthDate',
      'contactBirthCity',
      'contactBirthCountry',
      'lastExerciseOrForecastRevenue',
      'yearOfRevenue',
      'companyActivity',
      'isCompanyBeingCreated',
      'companySiret',
      'companySiren',
      { key: 'companyName', contactKey: ContactKey.COMPANY_NAME, forceReadonly: true },
      'companyNafCode',
      { key: 'companyNafLabel', forceReadonly: true },
      { key: 'companyCreationDate', forceReadonly: true },
      { key: 'companyLegalStatus1', forceReadonly: true },
      'companyLegalStatus2',
      'companyLegalStatus3',
      { key: 'companyHeadquarterAddress', forceReadonly: true },
      { key: 'companyHeadquarterPostcode', forceReadonly: true },
      { key: 'companyHeadquarterCity', forceReadonly: true },
      'companyHeadquarterCountry',
      ...Array.from({ length: 5 }, (_, index) => index + 1).flatMap(index => [
        `beneficialOwnerFirstName${index}`,
        `beneficialOwnerLastName${index}`,
        `beneficialOwnerBirthCity${index}`,
        `beneficialOwnerBirthCountry${index}`,
      ]),
    ],
  },
  policyInformationCard: {
    title: 'Informations complémentaires',
    fieldsConfig: [
      'specificQuestionGenerali2',
      'specificQuestionGenerali3',
      'specificQuestionGenerali4',
      'specificQuestionGenerali5',
      'specificQuestionGenerali6',
      'specificQuestionGenerali7',
      'specificQuestionGenerali8',
      'specificQuestionGenerali9',
    ],
  },
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: true,
    isQuoteTierModuleVisible: false,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          label: 'Envoi du contrat au client',
          icon: 'mail',
          successMessage: 'Contrat envoyé par email',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-supporting-validation',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'wholesaler-subscription-form-uploaded',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ documents }) =>
          documents.some(({ category }) => category === 'SUBSCRIPTION_FORM'),
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },
  enableAssetComparison: false,
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/transport-marchandises-generali/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/transport-marchandises-generali/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: false,
    isCompareDisabled: true,
  },
  policyholderInvitationModuleConfig: {
    relationTypes: policyHolderInvitationRole,
  },
} satisfies KorintModulesProductsConfig;
